import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

const button = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium',
    'transition-[background-color,border-color,color,box-shadow,transform]',
    'duration-150 ease-[cubic-bezier(0.32,0.72,0,1)]',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:translate-y-[0.5px]',
  ],
  {
    variants: {
      variante: {
        primaria:
          'bg-[var(--brand)] text-[var(--brand-texto)] shadow-[var(--shadow-xs)] hover:bg-[var(--brand-forte)]',
        secundaria:
          'bg-[var(--cor-superficie)] text-[var(--cor-texto)] border border-[var(--cor-borda-forte)] hover:bg-[var(--cor-superficie-hover)]',
        suave:
          'bg-[var(--brand-suave)] text-[var(--brand-contraste)] hover:brightness-[0.97]',
        fantasma: 'text-[var(--cor-texto-suave)] hover:bg-[var(--cor-superficie-2)] hover:text-[var(--cor-texto)]',
        perigo: 'bg-[var(--status-recusado)] text-white hover:brightness-110',
      },
      tamanho: {
        // Alvo de toque mínimo de 44 px no celular (seção 9.5).
        pequeno: 'h-9 min-h-9 rounded-[var(--radius-sm)] px-3 text-sm',
        medio: 'h-11 min-h-11 rounded-[var(--radius-md)] px-4 text-sm',
        grande: 'h-12 min-h-12 rounded-[var(--radius-md)] px-5 text-base',
        icone: 'h-11 w-11 min-h-11 min-w-11 rounded-[var(--radius-md)]',
      },
      largura: {
        auto: '',
        cheia: 'w-full',
      },
    },
    defaultVariants: { variante: 'primaria', tamanho: 'medio', largura: 'auto' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  carregando?: boolean;
  iconeEsquerda?: ReactNode;
  iconeDireita?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variante, tamanho, largura, carregando, iconeEsquerda, iconeDireita, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(button({ variante, tamanho, largura }), className)}
      disabled={disabled || carregando}
      aria-busy={carregando || undefined}
      {...props}
    >
      {carregando ? <Loader2 aria-hidden className="size-4 animate-spin" /> : iconeEsquerda}
      {children}
      {!carregando && iconeDireita}
    </button>
  );
});
